import { navigate } from '@reach/router';
import type { History } from '@reach/router';
import type { Tokens } from '@unitedtraders/keycloak';
import type { CurrentLang, GaIdProvider, UtIdProvider } from '@unitedtraders/utex-site-common';
import type { VisitCollector } from '@unitedtraders/visit-collector';
import { injectable } from 'inversify';
import type { App } from '@dizraptors/auth-microfrontend';
import { makeObservable, observable, runInAction, when } from 'mobx';
import type { AppConfig } from '../types/AppConfig';
import type { IZendeskChatApi } from '../types/Zendesk';
import { SessionService } from './SessionService';
import { locationToString } from '../utils/locationToString';
import type { PodsightsApi } from './podsightsProvider';
import { QueryParam } from '../conts/QueryParam';
import type { IsUserAuthorized } from './isUserAuthorized';

const uuidv4Regexp = new RegExp(
  /([0-9A-F]{8}-[0-9A-F]{4}-4[0-9A-F]{3}-[89AB][0-9A-F]{3}-[0-9A-F]{12})/i
);

@injectable()
export class AuthModalService {
  @observable
  currentModalScreen = '';

  private authApp: Promise<App>;

  private restorePasswordToken?: string;

  constructor(
    private location: Location,
    history: History,
    private config: AppConfig,
    private sessionService: SessionService,
    private currentLang: CurrentLang,
    private gaIdProvider: GaIdProvider,
    private utIdProvider: UtIdProvider,
    private zendeskApi: Promise<IZendeskChatApi>,
    private visitCollector: VisitCollector,
    private podsightsApi: Promise<PodsightsApi>,
    isUserAuthorized: IsUserAuthorized
  ) {
    makeObservable(this);
    history.listen(() => {
      const searchParams = new URLSearchParams(this.location.search);
      runInAction(() => {
        this.currentModalScreen = searchParams.get('modalScreen') as any;
      });
    });

    const token = this.getTokenFromUrl();

    if (token) {
      this.restorePasswordToken = token;
      this.showRestorePassword();
    }

    this.authApp = this.initAuthApp();
    this.authApp.then(app => {
      when(
        () => isUserAuthorized() !== undefined,
        () => {
          if (!isUserAuthorized()) {
            app.api.showGoogleOneTap();
          }
        }
      );
    });
  }

  async getParcelConfig() {
    const app = await this.authApp;
    return app.modalParcelConfig;
  }

  private changeScreen(screen: string) {
    const searchParams = new URLSearchParams(this.location.search);
    searchParams.set(QueryParam.screenKey, screen);
    searchParams.set(QueryParam.modalKey, 'auth');
    const newLocation = { ...this.location, search: searchParams.toString() };
    navigate(locationToString(newLocation));
  }

  showRestorePassword() {
    this.changeScreen('passwordRecovery');
  }

  showAuth() {
    this.changeScreen('main');
  }

  close() {
    const searchParams = new URLSearchParams(this.location.search);

    searchParams.delete(QueryParam.modalKey);
    searchParams.delete(QueryParam.screenKey);
    const newLocation = { ...this.location, search: searchParams.toString() };
    runInAction(() => {
      this.currentModalScreen = '';
    });
    navigate(locationToString(newLocation));
  }

  private getTokenFromUrl() {
    const passwordRecoveryMatch = this.location.pathname.match(/(.*)\/(password-recovery)/);
    if (!passwordRecoveryMatch) {
      return;
    }
    const tokenMatch = this.location.pathname.match(uuidv4Regexp);
    const tokenInUrl = tokenMatch?.[1];

    return tokenInUrl;
  }

  async initAuthApp() {
    const module = await import('@dizraptors/auth-microfrontend');

    return module.configure({
      // @ts-ignore
      locale: this.currentLang.langObserver,
      setTokens: (tokens: Tokens) => this.sessionService.setTokens(tokens),
      getTokens: () => this.sessionService.getTokens(),
      configs: {
        keycloak: {
          url: this.config.dizraptorGateway.rest?.url!, // eslint-disable-line
          realm: this.config.sessionService.realm,
          clientId: this.config.sessionService.clientId
        },
        sessionService: this.config.dizraptorGateway,
        dizraptorGateway: this.config.dizraptorGateway,
        appleAuth: this.config.appleAuth,
        googleAuth: this.config.googleAuth
      },
      getLanging: () => {
        return window.location.href;
      },
      featureFlags: {},
      getGaId: () => this.gaIdProvider.getId(),
      getClientId: () => this.utIdProvider.getId(),
      sendVisitCollectorEvent: this.visitCollector.send,
      openZendesk: () => {
        this.zendeskApi.then(api => api.show());
      },
      onScreenChange: screen => {
        this.changeScreen(screen);
      },
      closeModal: () => {
        this.close();
      },
      getRestorePasswordToken: () => {
        return this.restorePasswordToken;
      },
      onRegistrationSuccess: () => {
        this.podsightsApi.then(
          api => {
            api.lead({ category: 'Sign up' });
          },
          () => {
            console.error('Can`t send lead to podsight');
          }
        );
      }
    });
  }
}
